/**
 * ONE API client for camps and clinics.
 *
 * campsAPI.js and clinicsAPI.js were the same ~1,000 lines with a different
 * noun (Phase B, backend docs/EVENT_PRIMITIVE_CONSOLIDATION.md). They are now
 * the export names the app and tests import; the requests are built here, per
 * type, from typedEventNames (utils/typedEvents).
 */
import axios from "./axiosInstance";
import config from "./config";
import { typedEventNames } from "../utils/typedEvents";

const BASE_URL = config.BASE_URL;

/** @param {"camp" | "clinic"} type */
export function createTypedEventsAPI(type) {
  const T = typedEventNames(type);

  // Create a new camp
  const createTypedEvent = async (token, typedEventData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/create`,
        typedEventData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get all camps
  const getTypedEvents = async (token) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get camp by ID
  const getTypedEventById = async (token, typedEventId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Update camp
  const updateTypedEvent = async (token, typedEventId, typedEventData) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/${T.nouns}/${typedEventId}`,
        typedEventData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Delete camp
  const deleteTypedEvent = async (token, typedEventId) => {
    try {
      const response = await axios.delete(`${BASE_URL}/${T.nouns}/${typedEventId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Register for a camp
  const registerForTypedEvent = async (token, typedEventId, registrationData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/register`,
        registrationData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get camp registrations (admin)
  const getTypedEventRegistrations = async (token, typedEventId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/registrations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get my camp registrations
  const getMyTypedEventRegistrations = async (token) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/my-registrations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Preview what cancelling a registration would affect — invoice
  // state, payment state — so the confirmation modal can offer
  // "void invoice" and "refund payment" checkboxes contextually.
  const getTypedEventCancellationPreview = async (token, typedEventId, registrationId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/cancellation-preview`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Cancel a registration (staff, or the family's own). Staff options:
  //   - voidInvoice: void the linked charge.
  //   - refundPayment: implies voidInvoice; also refunds the payment through
  //     Stripe (needs refund_payments).
  //   - applyPolicy: refund what the event's refund policy gives back (needs
  //     refund_payments). A family sends none: the server applies the policy.
  const cancelTypedEventRegistration = async (token, typedEventId, registrationId, options = {}) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            void_invoice: !!options.voidInvoice,
            refund_payment: !!options.refundPayment,
            // Staff: refund what the event's refund policy gives back. A
            // family's own cancel always gets the policy, chosen by the server.
            apply_policy: !!options.applyPolicy,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Export camp registrations (admin)
  const exportTypedEventRegistrations = async (token, typedEventId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/export`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get camp attendance
  const getTypedEventAttendance = async (token, typedEventId, date) => {
    try {
      const params = date ? `?date=${date}` : "";
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/attendance${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Check in attendee
  const checkInTypedEventAttendee = async (token, typedEventId, registrationId, date) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/check-in`,
        { date },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Check out attendee
  const checkOutTypedEventAttendee = async (token, typedEventId, registrationId, date) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/check-out`,
        { date },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Mark attendee absent for a day (Phase D2: the shared attendance roster's Absent)
  const markTypedEventAttendeeAbsent = async (token, typedEventId, registrationId, date, notes) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/mark-absent`,
        { date, ...(notes !== undefined ? { notes } : {}) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Undo a day's check-in (Phase D2: clicking Present again on the shared roster)
  const undoTypedEventCheckIn = async (token, typedEventId, registrationId, date) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/undo-check-in`,
        { date },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Duplicate camp (admin)
  const duplicateTypedEvent = async (token, typedEventId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/duplicate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get QR code for a camp registration
  const getRegistrationQRCode = async (token, typedEventId, registrationId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/qr-code`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Validate QR code for camp check-in/out
  const validateTypedEventQRCode = async (token, qrCodeToken, typedEventId, date, action = "check_in") => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/validate-qr`,
        { qr_code_token: qrCodeToken, [T.fk]: typedEventId, date, action },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // TEMPLATE FUNCTIONS
  // =====================================================

  // Get all camp templates
  const getTypedEventTemplates = async (token) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/templates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get template by ID
  const getTypedEventTemplateById = async (token, templateId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/templates/${templateId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Create a template (from camp or directly)
  const createTypedEventTemplate = async (token, templateData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/templates`,
        templateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Update a template
  const updateTypedEventTemplate = async (token, templateId, templateData) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/${T.nouns}/templates/${templateId}`,
        templateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Delete a template
  const deleteTypedEventTemplate = async (token, templateId) => {
    try {
      const response = await axios.delete(`${BASE_URL}/${T.nouns}/templates/${templateId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Create a camp from template
  const createTypedEventFromTemplate = async (token, templateId, typedEventData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/templates/${templateId}/create-${T.noun}`,
        typedEventData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // WAITLIST FUNCTIONS
  // =====================================================

  // Get waitlist for a camp
  const getTypedEventWaitlist = async (token, typedEventId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/waitlist`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Promote from waitlist
  /**
   * @param {string|null} token
   * @param {string} campId
   * @param {string|null} [registrationId] - specific reg to promote; null = next in line
   */
  const promoteFromWaitlist = async (token, typedEventId, registrationId = null) => {
    try {
      const url = registrationId
        ? `${BASE_URL}/${T.nouns}/${typedEventId}/waitlist/${registrationId}/promote`
        : `${BASE_URL}/${T.nouns}/${typedEventId}/waitlist/promote`;
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // STAFF FUNCTIONS
  // =====================================================

  // Get staff assigned to a camp
  const getTypedEventStaff = async (token, typedEventId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/staff`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get available staff for assignment
  const getAvailableStaff = async (token, typedEventId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/available-staff`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Assign staff to a camp
  const assignTypedEventStaff = async (token, typedEventId, staffId, role = "staff") => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/staff`,
        { staff_id: staffId, role },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Update staff role
  const updateTypedEventStaffRole = async (token, typedEventId, assignmentId, role) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/${T.nouns}/${typedEventId}/staff/${assignmentId}`,
        { role },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Remove staff from a camp
  const removeTypedEventStaff = async (token, typedEventId, assignmentId) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/${T.nouns}/${typedEventId}/staff/${assignmentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // REPORTS & ANALYTICS FUNCTIONS
  // =====================================================

  // Get attendance report for a camp
  const getAttendanceReport = async (token, typedEventId, startDate = null, endDate = null) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      const queryString = params.toString() ? `?${params.toString()}` : "";

      const response = await axios.get(
        `${BASE_URL}/${T.nouns}/${typedEventId}/attendance-report${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get analytics for a specific camp
  const getTypedEventAnalytics = async (token, typedEventId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/analytics`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get organization-wide camp statistics
  const getTypedEventStats = async (token) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // EMAIL NOTIFICATION FUNCTIONS
  // =====================================================

  // Send camp reminder emails (admin)
  const sendTypedEventReminders = async (token, daysBefore = 3) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/send-reminders?days_before=${daysBefore}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Send custom notification to camp registrants (admin)
  const sendTypedEventNotification = async (token, typedEventId, notificationData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/notify`,
        notificationData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // DAILY UPDATES FUNCTIONS
  // =====================================================

  // Get daily updates for a camp
  const getDailyUpdates = async (token, typedEventId, date = null) => {
    try {
      const params = date ? `?date=${date}` : "";
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/daily-updates${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Save a daily update for a registration
  const saveDailyUpdate = async (token, typedEventId, registrationId, updateData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/daily-update`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Send daily update emails (admin)
  const sendDailyUpdateEmails = async (token, typedEventId, date) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/send-daily-updates`,
        { date },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // PAYMENT FUNCTIONS
  // =====================================================

  // Create a payment intent for camp registration
  // The server works out what is owed from the family's own registration and
  // charges the card-processor customer on the family's record. `amount` is
  // what this page showed: a different figure is refused (409 PRICE_CHANGED)
  // with the right one. `registrationId` says WHICH registration is being paid.
  const createTypedEventPaymentIntent = async (token, typedEventId, membershipId, amount, registrationId = null) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/payments/create-intent`,
        { [`${T.noun}Id`]: typedEventId, membershipId, amount, ...(registrationId ? { registrationId } : {}) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Confirm payment after successful Stripe payment
  const confirmTypedEventPayment = async (token, registrationId, paymentIntentId, transactionId = null) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/payments/confirm`,
        { registrationId, paymentIntentId, transactionId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Process refund for a camp registration
  const processTypedEventRefund = async (token, registrationId, refundAmount = null, reason = null) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/payments/refund`,
        { registrationId, refundAmount, reason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Batch register multiple athletes for a camp
  const batchRegisterForTypedEvent = async (token, typedEventId, data) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/batch-register`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Confirm batch payment after successful Stripe payment
  const confirmBatchTypedEventPayment = async (token, paymentIntentId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/payments/confirm-batch`,
        { paymentIntentId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get payment status for a registration
  const getTypedEventPaymentStatus = async (token, typedEventId, registrationId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/payment-status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Admin: update registration payment status (mark paid, waive)
  const adminUpdateTypedEventPaymentStatus = async (token, typedEventId, registrationId, paymentStatus, note = null) => {
    try {
      const response = await axios.patch(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/payment-status`,
        { payment_status: paymentStatus, note },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Record a payment for a camp registration (admin/staff)
  const recordTypedEventRegistrationPayment = async (token, typedEventId, registrationId, paymentData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/${registrationId}/record-payment`,
        paymentData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // PHOTO GALLERY FUNCTIONS
  // =====================================================

  // Get all photos for a camp
  const getTypedEventPhotos = async (token, typedEventId, date = null) => {
    try {
      const params = date ? `?date=${date}` : "";
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/photos${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Add a single photo to a camp
  const addTypedEventPhoto = async (token, typedEventId, photoData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/photos`,
        photoData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Add multiple photos to a camp
  const addTypedEventPhotos = async (token, typedEventId, photos, dateTaken = null) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/photos/bulk`,
        { photos, date_taken: dateTaken },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Update a photo
  const updateTypedEventPhoto = async (token, typedEventId, photoId, updateData) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/${T.nouns}/${typedEventId}/photos/${photoId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Delete a photo
  const deleteTypedEventPhoto = async (token, typedEventId, photoId) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/${T.nouns}/${typedEventId}/photos/${photoId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Set a photo as featured
  const setFeaturedTypedEventPhoto = async (token, typedEventId, photoId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/photos/${photoId}/featured`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Reorder photos
  const reorderTypedEventPhotos = async (token, typedEventId, photoOrder) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/photos/reorder`,
        { photoOrder },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // =====================================================
  // SUPPLY CHECKLIST FUNCTIONS
  // =====================================================

  // Get supply list for a camp
  const getTypedEventSupplyList = async (token, typedEventId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${T.nouns}/${typedEventId}/supplies`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Update supply list for a camp
  const updateTypedEventSupplyList = async (token, typedEventId, supplyData) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/${T.nouns}/${typedEventId}/supplies`,
        supplyData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Add an item to supply list
  const addSupplyItem = async (token, typedEventId, itemData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/supplies`,
        itemData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Send bulk email to selected camp registrations
  const sendBulkTypedEventRegistrationEmail = async (token, typedEventId, data) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/registrations/email`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Remove an item from supply list
  const removeSupplyItem = async (token, typedEventId, itemId) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/${T.nouns}/${typedEventId}/supplies/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Admin register a member/athlete for a camp
  const adminRegisterForTypedEvent = async (token, typedEventId, registrationData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${T.nouns}/${typedEventId}/admin/register`,
        registrationData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  return {
    createTypedEvent,
    getTypedEvents,
    getTypedEventById,
    updateTypedEvent,
    deleteTypedEvent,
    registerForTypedEvent,
    getTypedEventRegistrations,
    getMyTypedEventRegistrations,
    getTypedEventCancellationPreview,
    cancelTypedEventRegistration,
    exportTypedEventRegistrations,
    getTypedEventAttendance,
    checkInTypedEventAttendee,
    checkOutTypedEventAttendee,
    markTypedEventAttendeeAbsent,
    undoTypedEventCheckIn,
    duplicateTypedEvent,
    getRegistrationQRCode,
    validateTypedEventQRCode,
    getTypedEventTemplates,
    getTypedEventTemplateById,
    createTypedEventTemplate,
    updateTypedEventTemplate,
    deleteTypedEventTemplate,
    createTypedEventFromTemplate,
    getTypedEventWaitlist,
    promoteFromWaitlist,
    getTypedEventStaff,
    getAvailableStaff,
    assignTypedEventStaff,
    updateTypedEventStaffRole,
    removeTypedEventStaff,
    getAttendanceReport,
    getTypedEventAnalytics,
    getTypedEventStats,
    sendTypedEventReminders,
    sendTypedEventNotification,
    getDailyUpdates,
    saveDailyUpdate,
    sendDailyUpdateEmails,
    createTypedEventPaymentIntent,
    confirmTypedEventPayment,
    processTypedEventRefund,
    batchRegisterForTypedEvent,
    confirmBatchTypedEventPayment,
    getTypedEventPaymentStatus,
    adminUpdateTypedEventPaymentStatus,
    recordTypedEventRegistrationPayment,
    getTypedEventPhotos,
    addTypedEventPhoto,
    addTypedEventPhotos,
    updateTypedEventPhoto,
    deleteTypedEventPhoto,
    setFeaturedTypedEventPhoto,
    reorderTypedEventPhotos,
    getTypedEventSupplyList,
    updateTypedEventSupplyList,
    addSupplyItem,
    sendBulkTypedEventRegistrationEmail,
    removeSupplyItem,
    adminRegisterForTypedEvent,
  };
}
